import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Heart, X } from "lucide-react";
import useFavourites from "@/hooks/useFavourites";
import { resolveImage, sendInquiry, formatApiErrorDetail } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ARTIST_EMAIL = "info@wwabstract.com";

function ShortlistRow({ p, onRemove }) {
  return (
    <li className="favs-row" data-testid={`favs-row-${p.slug}`}>
      <Link to={`/paintings/${p.slug}`} className="favs-row__thumb" state={{ from: "grid" }}>
        <img src={resolveImage(p.image_url)} alt={p.alt || p.title} loading="lazy" />
      </Link>
      <div className="favs-row__body">
        <Link to={`/paintings/${p.slug}`} className="favs-row__title" state={{ from: "grid" }}>{p.title}</Link>
        <span className="favs-row__meta mono-caps">{p.medium} · {p.dimensions_display || p.dimensions}{p.price_display ? ` · ${p.price_display}` : ""}</span>
      </div>
      <button type="button" className="favs-row__remove" onClick={() => onRemove(p.slug)} aria-label={`Remove ${p.title}`} data-testid={`favs-remove-${p.slug}`}>
        <X size={14} strokeWidth={1.5} />
      </button>
    </li>
  );
}

function SendForm({ slugs, onSent }) {
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await sendInquiry({ ...form, slugs });
      onSent();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };
  return (
    <form className="favs-form" onSubmit={submit} data-testid="favs-form">
      <p className="mono-caps favs-form__label">Or send your shortlist to the artist</p>
      <div className="favs-form__row">
        <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="favs-name" />
        <input required type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="favs-email" />
      </div>
      <textarea rows={3} placeholder="A note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="favs-note" />
      {error && <p className="favs-form__error" data-testid="favs-error">{error}</p>}
      <button type="submit" className="favs-cta favs-cta--solid mono-caps" disabled={busy} data-testid="favs-send">
        {busy ? "Sending…" : "Send to the artist"}
      </button>
    </form>
  );
}

// Floating "♡ n" pill + slide-in drawer listing the visitor's shortlist.
export default function FavouritesWidget() {
  const { slugs, count, remove, clear } = useFavourites();
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState([]);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open || all.length) return;
    axios.get(`${API}/paintings`).then((r) => setAll(r.data)).catch(() => {});
  }, [open, all.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const items = useMemo(
    () => slugs.map((s) => all.find((p) => p.slug === s)).filter(Boolean),
    [slugs, all]
  );

  const mailto = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const lines = items.map((p) => `• ${p.title}${p.price_display ? ` — ${p.price_display}` : ""}\n  ${origin}/paintings/${p.slug}`);
    const body = `Hello Warren,\n\nI've been looking at these works and would love to hear more:\n\n${lines.join("\n\n")}\n\nThank you,\n`;
    return `mailto:${ARTIST_EMAIL}?subject=${encodeURIComponent(`Shortlist — ${items.length} work${items.length === 1 ? "" : "s"}`)}&body=${encodeURIComponent(body)}`;
  }, [items]);

  return (
    <>
      <button
        type="button"
        className={`favs-pill ${count ? "has-items" : ""}`}
        onClick={() => setOpen(true)}
        aria-label={`Open your shortlist (${count})`}
        data-testid="favs-pill"
      >
        <Heart className="favs-pill__ico" strokeWidth={1.5} />
        <span className="favs-pill__count" data-testid="favs-count">{count}</span>
      </button>

      {open && (
        <div className="favs-drawer" role="dialog" aria-modal="true" aria-label="Your shortlist" data-testid="favs-drawer">
          <button className="favs-drawer__scrim" onClick={() => setOpen(false)} aria-label="Close shortlist" />
          <aside className="favs-drawer__panel">
            <header className="favs-drawer__top">
              <div>
                <p className="mono-caps favs-drawer__over">Your shortlist</p>
                <h2 className="favs-drawer__title">{count ? `${count} work${count === 1 ? "" : "s"} you've kept` : "Nothing kept yet"}</h2>
              </div>
              <button type="button" className="favs-drawer__close" onClick={() => setOpen(false)} aria-label="Close" data-testid="favs-close">
                <X size={18} strokeWidth={1.5} />
              </button>
            </header>

            {count === 0 ? (
              <p className="favs-empty" data-testid="favs-empty">Tap the heart on any painting to keep it here while you wander.</p>
            ) : (
              <>
                <ul className="favs-list" data-testid="favs-list">
                  {items.map((p) => <ShortlistRow key={p.slug} p={p} onRemove={remove} />)}
                </ul>

                <div className="favs-actions">
                  <a href={mailto} className="favs-cta mono-caps" data-testid="favs-mailto">Email the artist</a>
                  <button type="button" className="favs-clear mono-caps" onClick={clear} data-testid="favs-clear">Clear all</button>
                </div>

                {sent ? (
                  <p className="favs-sent" data-testid="favs-sent">Sent. Warren will write back to you personally.</p>
                ) : (
                  <SendForm slugs={slugs} onSent={() => setSent(true)} />
                )}
              </>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
